import type { PublicUser } from '@bcl/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchMe } from '@/features/auth/auth-api';
import { getToken, setToken } from './api';

type AuthState = {
  user: PublicUser | null;
  isLoading: boolean;
  setSession: (token: string, user: PublicUser) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  children,
  initialUser = null,
}: {
  children: ReactNode;
  initialUser?: PublicUser | null;
}) {
  const [user, setUser] = useState<PublicUser | null>(initialUser);
  const hasToken = Boolean(getToken());
  const [isLoading, setIsLoading] = useState(initialUser ? false : hasToken);

  // Si un JWT est présent en localStorage mais qu'on n'a pas encore
  // l'utilisateur en mémoire (reload, ouverture d'un nouvel onglet,
  // screenshot headless avec storage pré-injecté), on hydrate via /auth/me.
  useEffect(() => {
    if (user || !hasToken) return;
    let cancelled = false;
    fetchMe()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) {
          setToken(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, hasToken]);

  const setSession = useCallback((token: string, u: PublicUser) => {
    setToken(token);
    setUser(u);
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, setSession, signOut }),
    [user, isLoading, setSession, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
